import { AppUpdate, AppUpdateAvailability, AppUpdateInfo, AppUpdateResultCode } from '@capawesome/capacitor-app-update';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { ref } from 'vue';

// Numeric App Store id of the production app (com.time-it.app)
const IOS_APP_STORE_ID = '6466124526';
// The app is only published in the Israeli App Store; the iTunes lookup defaults to the US store
const IOS_STORE_COUNTRY = 'il';

export const storeUpdateState = {
  updateRequired: ref(false),
  availableVersion: ref<string | undefined>(undefined),
  isOpeningStore: ref(false)
};

let isChecking = false;

// Returns the device iOS version (e.g. "17.2") from the WebView user agent
function getIosVersion(): string | undefined {
  const match = navigator.userAgent.match(/OS (\d+)[_.](\d+)(?:[_.](\d+))?/);
  return match ? match.slice(1).filter(Boolean).join('.') : undefined;
}

// Numeric compare of dotted versions: negative if a < b, 0 if equal, positive if a > b
function compareVersions(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true });
}

function isUpdateInstallable(info: AppUpdateInfo): boolean {
  if (Capacitor.getPlatform() !== 'ios' || !info.minimumOsVersion) return true;
  const iosVersion = getIosVersion();
  // Never lock out a device that can't install the new version
  return !iosVersion || compareVersions(iosVersion, info.minimumOsVersion) >= 0;
}

export class StoreUpdateService {
  /**
   * Checks the App Store / Google Play for a newer native version and, if one exists,
   * raises the blocking update screen. Fails open: any lookup error leaves the app usable.
   */
  static async checkForStoreUpdate(): Promise<void> {
    if (!Capacitor.isNativePlatform() || import.meta.env.VITE_ENV === 'local' || isChecking) return;
    isChecking = true;

    try {
      const info = await AppUpdate.getAppUpdateInfo(
        Capacitor.getPlatform() === 'ios' ? { country: IOS_STORE_COUNTRY } : undefined
      );

      const updateAvailable = info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE
        || info.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS;

      storeUpdateState.availableVersion.value = info.availableVersionName;
      storeUpdateState.updateRequired.value = updateAvailable && isUpdateInstallable(info);
    } catch (error) {
      // Staging bundles aren't in the stores and the lookup can fail offline — don't block
      console.warn('Failed to check for store update:', error);
    } finally {
      isChecking = false;
    }
  }

  static async openStore(): Promise<void> {
    storeUpdateState.isOpeningStore.value = true;
    try {
      if (Capacitor.getPlatform() === 'android') {
        try {
          // Google Play's native full-screen update flow; fall back to the store page if it can't run.
          // A user cancel just returns to the blocking screen.
          const { code } = await AppUpdate.performImmediateUpdate();
          if (code === AppUpdateResultCode.OK || code === AppUpdateResultCode.CANCELED) return;
        } catch (error) {
          console.warn('Immediate update unavailable, opening Play Store:', error);
        }
        await AppUpdate.openAppStore();
      } else {
        await AppUpdate.openAppStore({ appId: IOS_APP_STORE_ID });
      }
    } catch (error) {
      console.error('Failed to open app store:', error);
    } finally {
      storeUpdateState.isOpeningStore.value = false;
    }
  }

  /** Checks on launch and every time the app returns to the foreground. */
  static init(): void {
    if (!Capacitor.isNativePlatform()) return;

    StoreUpdateService.checkForStoreUpdate();
    CapacitorApp.addListener('resume', () => StoreUpdateService.checkForStoreUpdate());
  }
}
